import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { composeClassName } from '@/utils/util_helpers';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';
import { useWatchToggle } from '@/hooks/useWatchToggle';
import ShareModal from '@/components/share-modal';
import { format } from 'date-fns';
import { useWindowDimensions } from '@/hooks/use-window-dimensions';

interface HouseOverviewProps {
  title: string;
  address: string;
  descriptionHtml: string;
  aboutNeighbourhoodHtml?: string;
  stats: {
    bedrooms: number;
    bathrooms: number;
    toilets: number;
    livable_space: number;
    launch_date: string;
  };
  houseInfo: {
    rent: number;
    minRent: number;
    maxRent: number;
    charges: number;
    preRegistered: number;
  };
  className?: string;
  slug: string;
  isWatched?: boolean;
}

const HouseOverview: React.FC<HouseOverviewProps> = ({
  title,
  address,
  descriptionHtml,
  aboutNeighbourhoodHtml,
  stats,
  houseInfo,
  className,
  slug,
  isWatched: initialIsWatched = false,
}) => {
  const { t } = useTranslation();
  const { handleWatchToggle } = useWatchToggle();
  const [isWatched, setIsWatched] = useState(initialIsWatched);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const { screenIsLargeOrEqualThan } = useWindowDimensions();
  const isMobile = !screenIsLargeOrEqualThan('sm');

  const onSaveClick = () => {
    if (!slug || isLoading) return;

    setIsLoading(true);
    handleWatchToggle(slug, isWatched)
      .then(() => {
        setIsWatched(!isWatched);
      })
      .catch((error) => {
        console.error('Failed to toggle watch:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const toggleDescription = () => {
    setIsExpanded(!isExpanded);
  };

  const handleShareClick = () => {
    setIsShareModalOpen(true);
  };

  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
  };

  return (
    <div className={composeClassName('mx-auto', className)}>
      {/* Header */}
      <div className="mb-8 flex flex-col items-start justify-between gap-3 md:flex-row md:items-center md:gap-0">
        <div className="w-full md:w-auto">
          <div className="flex flex-col items-start gap-2 md:flex-row md:items-center md:gap-3">
            <h1
              className={composeClassName(
                'font-sans font-bold leading-tight text-N900',
                isMobile ? 'text-[22px]' : 'text-[32px]'
              )}
            >
              {title}
            </h1>
            {/* <Box
              sx={{
                padding: '6px 16px',
                borderRadius: 999,
                background:
                  'linear-gradient(22.08deg, #F71818 -22.56%, #F9441B -9.98%, #FB7B1E 14.62%, #FEC600 58.21%, #FECA06 66.97%, #FED50E 75.17%, #FEE916 83.15%, #FFFF25 89.78%)',
              }}
              className="font-sofia-pro mt-2 text-[14px] font-medium leading-[21px] text-N900 md:mt-0"
            >
              {t('js.components.house_card.coming_soon')}
            </Box> */}
          </div>
          {/* Address */}
          <div
            style={{ fontSize: '16px' }}
            className="lead2 mb-3 flex items-center gap-2 text-N900 md:mb-7"
          >
            {IconSaxIcon({ name: 'location', size: 15, strokeWidth: 1.5 })}
            <span
              className={composeClassName(
                'font-sans font-normal text-N900',
                isMobile ? 'text-[14px]' : 'text-[18px]'
              )}
            >
              {address}
            </span>
          </div>
          <div
            className="markdown-content font-sofia-pro mt-2 text-[16px] font-normal leading-[24px] text-N900 md:mt-0 md:text-[22px] md:leading-[30px]"
            dangerouslySetInnerHTML={{ __html: descriptionHtml }}
          />
        </div>
        <div className="flex gap-2 md:mt-0 md:w-auto md:gap-4">
          <button
            type="button"
            className={composeClassName(
              'flex flex-1 items-center justify-center rounded-sm bg-gray-50 text-gray-700 hover:bg-gray-100 md:flex-none md:p-[11px]',
              isMobile ? 'px-3 py-2' : 'gap-2 p-2'
            )}
            onClick={handleShareClick}
          >
            <span className="text-[14px] font-medium">
              {!isMobile ? t('js.components.house_overview.share') : ''}
            </span>
            {IconSaxIcon({ name: 'share', size: 15, strokeWidth: 1.5 })}
          </button>
          <button
            type="button"
            className={composeClassName(
              'flex flex-1 items-center justify-center rounded-sm text-gray-700 hover:bg-gray-100 md:flex-none md:p-[11px]',
              'bg-gray-50',
              isLoading ? 'cursor-not-allowed opacity-50' : '',
              isMobile ? 'px-3 py-2' : 'gap-2 p-2'
            )}
            onClick={onSaveClick}
            disabled={isLoading}
          >
            <span className="text-[14px] font-medium">
              {!isMobile
                ? isLoading
                  ? t('js.components.house_overview.saving')
                  : isWatched
                  ? t('js.components.house_overview.saved')
                  : t('js.components.house_overview.save')
                : ''}
            </span>
            {IconSaxIcon({
              name: isWatched ? 'heart-red' : 'heart',
              size: 15,
              strokeWidth: 1.5,
              strokeColor: isWatched ? 'none' : '#1A1A1A',
            })}
          </button>
        </div>
      </div>

      {/* Community */}
      <div
        style={{
          background:
            'var(--Gradients-soft-accent, linear-gradient(95deg, var(--background-primary-accent-light, #FFFAE9) 0%, var(--background-primary-medium, #F8F9FA) 98.43%))',
          borderRadius: 'var(--border-radius-lg, 16px)',
          position: 'relative',
        }}
        className={composeClassName(
          'mb-8 flex flex-col  gap-4 rounded-lg bg-N100 px-3 py-4 md:mb-7 md:flex-row md:gap-6 md:px-6 md:py-5',
          isMobile ? 'items-start' : 'items-center'
        )}
      >
        <div className="mb-2 flex-shrink-0 md:mb-0">
          {IconSaxIcon({
            name: 'roomies',
            size: isMobile ? 64 : 104,
            strokeWidth: 1.5,
            strokeColor: 'none',
          })}
        </div>
        <div>
          <h2
            className={composeClassName(
              'mb-1 font-bold text-N900',
              isMobile ? 'text-[20px]' : 'text-[22px]'
            )}
          >
            {t('js.components.house_overview.community_title')}
          </h2>
          <p
            className={composeClassName(
              'lead2 text-h6 mb-2 font-medium text-N900',
              isMobile ? 'text-[14px]' : 'text-[16px]'
            )}
          >
            {t('js.components.house_overview.community_subtitle')}
          </p>
          <p
            className={composeClassName(
              'lead2 text-h6 font-normal text-J300',
              isMobile ? 'text-[14px]' : 'text-[14px]'
            )}
          >
            {t('js.components.house_overview.pre_registered', { count: houseInfo.preRegistered })}
          </p>
        </div>
      </div>

      {/* Pricing */}
      <h2
        className={composeClassName(
          'font-sofia-pro mb-4 font-bold leading-[30px] text-N900',
          isMobile ? 'text-lead1' : 'text-[22px]'
        )}
      >
        {t('js.components.house_overview.pricing')}
      </h2>
      <div className="mb-8 flex flex-col gap-4 md:mb-7 md:flex-row md:gap-8">
        <div className="flex flex-1 flex-col rounded-lg bg-N100 p-4">
          <h2
            style={{ fontSize: isMobile ? '14px' : '16px' }}
            className="lead2 mb-1 font-medium text-N900"
          >
            {t('js.components.house_overview.rent_starting_from')}
          </h2>
          <div className="flex items-baseline">
            <span className={composeClassName(isMobile ? 'text-[16px] font-bold' : 'text-h5')}>
              €{houseInfo.rent}
            </span>
            <span
              style={{ fontSize: isMobile ? '12px' : '16px' }}
              className="ml-1 font-normal text-N900"
            >
              {t('js.components.house_overview.per_month')}
            </span>
          </div>
          {houseInfo.minRent !== houseInfo.maxRent && (
            <p
              style={{ fontSize: isMobile ? '12px' : '16px' }}
              className="lead2 mb-1 mt-1 font-medium text-text-primaryMedium"
            >
              {t('js.components.house_overview.contribution_from_to', { min: houseInfo.minRent, max: houseInfo.maxRent })}
            </p>
          )}
        </div>
        <div className="flex flex-1 flex-col rounded-lg bg-N100 p-4">
          <h2
            style={{ fontSize: isMobile ? '14px' : '16px' }}
            className="lead2 mb-1 font-medium text-N900"
          >
            {t('js.components.house_overview.monthly_charges')}
          </h2>
          <div className="flex items-baseline">
            <span className={composeClassName(isMobile ? 'text-[16px] font-bold' : 'text-h5')}>
              €{houseInfo.charges}
            </span>
            <span
              style={{ fontSize: isMobile ? '12px' : '16px' }}
              className="ml-1 font-normal text-N900"
            >
              {t('js.components.house_overview.per_month')}
            </span>
          </div>
          <p
            style={{ fontSize: isMobile ? '12px' : '16px' }}
            className="lead2 mb-1 mt-1 font-medium text-text-primaryMedium"
          >
            {t('js.components.house_overview.charges_description')}
          </p>
        </div>
      </div>

      {/* Overview */}
      <h2
        className={composeClassName(
          'font-sofia-pro mb-4 font-bold leading-[30px] text-N900',
          isMobile ? 'text-lead1' : 'text-[22px]'
        )}
      >
        {t('js.components.house_overview.overview')}
      </h2>
      <div className="mb-8 flex flex-wrap gap-4">
        <div className="flex flex-1 flex-col items-center justify-center rounded-lg bg-N100 p-4">
          <span className={composeClassName(isMobile ? 'text-[16px] font-bold' : 'text-h6')}>
            {stats.bedrooms}
          </span>
          <span
            className={composeClassName(
              'text-center text-N700',
              isMobile ? 'text-[14px]' : 'text-[16px]'
            )}
          >
            {t(`js.components.house_overview.${stats.bedrooms === 1 ? 'bedroom' : 'bedrooms'}`)}
          </span>
        </div>
        <div className="flex flex-1 flex-col items-center justify-center rounded-lg bg-N100 p-4">
          <span className={composeClassName(isMobile ? 'text-[16px] font-bold' : 'text-h6')}>
            {stats.bathrooms}
          </span>
          <span
            className={composeClassName(
              'text-center text-N700',
              isMobile ? 'text-[14px]' : 'text-[16px]'
            )}
          >
            {t(`js.components.house_overview.${stats.bathrooms === 1 ? 'bathroom' : 'bathrooms'}`)}
          </span>
        </div>
        <div className="flex flex-1 flex-col items-center justify-center rounded-lg bg-N100 p-4">
          <span className={composeClassName(isMobile ? 'text-[16px] font-bold' : 'text-h6')}>
            {stats.toilets}
          </span>
          <span
            className={composeClassName(
              'text-center text-N700',
              isMobile ? 'text-[14px]' : 'text-[16px]'
            )}
          >
            {t(`js.components.house_overview.${stats.toilets === 1 ? 'toilet' : 'toilets'}`)}
          </span>
        </div>
        <div className="flex flex-1 flex-col items-center justify-center rounded-lg bg-N100 p-4">
          <span className={composeClassName(isMobile ? 'text-[16px] font-bold' : 'text-h6')}>
            {stats.livable_space} m<sup>2</sup>
          </span>
          <span
            className={composeClassName(
              'text-center text-N700',
              isMobile ? 'text-[14px]' : 'text-[16px]'
            )}
          >
            {t('js.components.house_overview.livable_space')}
          </span>
        </div>
        <div className="flex flex-1 flex-col items-center justify-center rounded-lg bg-N100 p-4">
          <span className={composeClassName(isMobile ? 'text-[16px] font-bold' : 'text-h6')}>
            {stats.launch_date
              ? format(new Date(stats.launch_date), 'd MMMM yyyy')
              : t('js.components.house_details.no_launch_date')}
          </span>
          <span
            className={composeClassName(
              'text-center text-N700',
              isMobile ? 'text-[14px]' : 'text-[16px]'
            )}
          >
            {t('js.components.house_overview.launch_date')}
          </span>
        </div>
      </div>

      {/* About */}
      {aboutNeighbourhoodHtml && (
        <>
          <h2
            className={composeClassName(
              'font-sofia-pro mb-4 font-bold leading-[30px] text-N900',
              isMobile ? 'text-lead1' : 'text-[22px]'
            )}
          >
            {t('js.components.house_overview.about')}
          </h2>
          <div
            className={composeClassName(
              'markdown-content mb-4 w-full font-normal text-N900 md:w-1/2',
              isMobile ? 'text-[14px]' : 'text-[16px]',
              !isExpanded && 'line-clamp-3'
            )}
            dangerouslySetInnerHTML={{ __html: aboutNeighbourhoodHtml }}
          />
          <button
            type="button"
            style={{ borderBottom: '1px solid' }}
            className="text-primary-light hover:bg-primary-light border-primary-100 font-sans text-[14px] font-medium text-N900"
            onClick={toggleDescription}
          >
            {t(`js.components.house_overview.${isExpanded ? 'show_less' : 'show_more'}`)}
          </button>
        </>
      )}

      {/* Share Modal */}
      <ShareModal
        open={isShareModalOpen}
        onClose={handleCloseShareModal}
        url={typeof window !== 'undefined' ? window.location.href : ''}
      />
    </div>
  );
};

export default HouseOverview;
